import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import {
	BaseOrderResponse,
	EasyState,
	IBatchCreateRequestModel,
	IBatchDeleteRequestModel,
	IBatchDeleteResponseModel
} from '@easy-lib/modules/order';
import { BaseService, NotificationService, NotificationType } from '../../../shared/services';
import { apiUrls } from '../../../../constants';
import { IBaseOMSOrder, IBaseOrderEntity } from '@easy-lib/modules/order';
import { DraftOrder } from '@easy-lib/modules/order';
import { guid } from '../../../../tools';
import { OrderValidation } from './order-validation';
import { OrderSending } from './order-sending';

@Injectable({
	providedIn: 'root'
})
export class DraftService extends BaseService {
	private notificationService: NotificationService = inject(NotificationService);
	private orderValidation: OrderValidation = inject(OrderValidation);
	private orderSending: OrderSending = inject(OrderSending);

	get orderUrl(): string {
		return this.mtsPath + apiUrls.oms;
	}
	private get draftUrl(): string {
		return this.mtsPath + apiUrls.easy + 'draft';
	}

	getDrafts(): Observable<IBaseOrderEntity[]> {
		return this.httpService.get<IBaseOrderEntity[]>(this.draftUrl, { guestUserRestriction: true }).pipe(
			map(res => res.map(t => new DraftOrder({ ...t, easyState: EasyState.Done }))),
			catchError(() => {
				return of([]);
			})
		);
	}

	createDraft(draft: IBaseOMSOrder): Observable<BaseOrderResponse> {
		return this.httpService.post<BaseOrderResponse>(this.draftUrl, { body: { draft } }).pipe(
			map(res => ({ ...res, isSuccessful: !!res?.id })),
			catchError(error => of({ isSuccessful: false, message: error }))
		);
	}

	updateDraft(draft: IBaseOMSOrder): Observable<BaseOrderResponse> {
		return this.httpService.put<BaseOrderResponse>(this.draftUrl, { body: { draft } }).pipe(
			map(res => ({ ...res, isSuccessful: !!res?.id })),
			catchError(error => of({ isSuccessful: false, message: error }))
		);
	}

	deleteDraft(id: string): Observable<BaseOrderResponse> {
		return this.httpService.delete(`${this.draftUrl}/${id}`).pipe(
			map(() => ({ isSuccessful: true })),
			catchError(error => of({ isSuccessful: false, message: error }))
		);
	}

	batchCreate(body: IBatchCreateRequestModel) {
		return this.httpService.post(this.orderUrl + 'order/batchCreate', { body }).pipe(
			catchError(error => {
				return of({ id: '', message: error?.error, isSuccessful: false } as BaseOrderResponse);
			})
		);
	}

	batchDelete(body: IBatchDeleteRequestModel): Observable<any> {
		return this.httpService
			.post<IBatchDeleteResponseModel>(this.orderUrl + 'order/batchDelete', { body })
			.pipe(map(batchDeleteResponse => batchDeleteResponse.draftIds));
	}

	prepareDraftToCreate(draft: DraftOrder, isCreate: boolean) {
		const result = this.orderValidation.doBasicOrderValidations(draft);
		if (!result.successful) {
			this.notify(result.errorMessage, NotificationType.warning);
			return { isSuccess: result.successful };
		}
		const newDraft = new DraftOrder({
			...draft,
			id: isCreate ? guid() : draft.id,
			createDateTime: new Date().toString(),
			easyState: EasyState.BeingCreated
		});
		delete newDraft['trades'];
		const sendingDraft = this.orderSending.prepareSendingDraft(draft, isCreate);
		return { isSuccess: result.successful, newDraft: newDraft, sendingDraft: sendingDraft };
	}
	notify(message: string, notificationType: NotificationType) {
		this.notificationService.alert(message, notificationType);
	}
}
